import { Check, Loader2, SendHorizontal, Sparkles, TriangleAlert, Undo2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { adminApi } from "../api/admin";
import { chatWithConnection, listAiConnections, type AiConnection } from "../api/aiConnections";
import { ApiError } from "../api/client";
import { ownerApi } from "../api/owner";
import type { AiChatHistoryItem } from "../types";
import type { SDef } from "./model";
import "./aiChat.css";

interface Msg {
  id: string;
  role: "user" | "assistant";
  text: string;
  changes?: string[];
  warnings?: string[];
  /** The AI edit this message describes is in the editor (can be undone from here). */
  applied?: boolean;
  undone?: boolean;
  error?: boolean;
  connectionName?: string;
}

export interface AiSelectedWidget {
  id: string;
  type: string;
  title?: string | null;
}

interface Props {
  open: boolean;
  mode: "admin" | "owner";
  reportId: string;
  pageName: string;
  selectedWidgets: AiSelectedWidget[];
  /** The live definition + active page at the moment of sending. */
  getSnapshot: () => { def: SDef; pageId: string } | null;
  /** Put the AI's definition into the editor as ONE undoable step (3-way merged by the caller). */
  onApply: (sent: SDef, ai: SDef, changeCount: number) => void;
  /** Undo the last AI edit if nothing else was edited since; returns whether it did. */
  onUndo: () => boolean;
  onClose: () => void;
}

const MAX_STORED = 40;
const storageKey = (reportId: string) => `mrp-ai-chat:${reportId}`;
const uid = () => `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function loadMessages(reportId: string): Msg[] {
  try {
    const raw = window.sessionStorage.getItem(storageKey(reportId));
    const parsed = raw ? (JSON.parse(raw) as Msg[]) : [];
    // An undo button from a previous page load would point at nothing.
    return Array.isArray(parsed) ? parsed.map((m) => ({ ...m, applied: false })) : [];
  } catch {
    return [];
  }
}

function saveMessages(reportId: string, messages: Msg[]): void {
  try {
    window.sessionStorage.setItem(storageKey(reportId), JSON.stringify(messages.slice(-MAX_STORED)));
  } catch {
    /* private mode / quota: the chat simply isn't remembered */
  }
}

const CHART_TYPES = /chart|map$|^gauge|^progress$/;

function suggestionsFor(selected: AiSelectedWidget[]): string[] {
  if (selected.length === 0) return ["Tidy the layout", "Change all card header colours", "Add a KPI row", "Make all fonts a bit larger"];
  const types = new Set(selected.map((w) => w.type));
  if (types.size === 1) {
    const t = [...types][0];
    if (t === "table" || t === "matrix") return ["Make the header bold", "Set equal column widths", "Make the text bigger", "Make the header navy"];
    if (t === "kpi") return ["Make the number bigger", "Change the card colour", "Show it as a percentage", "Make it wider"];
    if (CHART_TYPES.test(t)) return ["Make the legend smaller", "Convert to a pie chart", "Show data labels", "Make it bigger"];
  }
  return ["Align these neatly", "Make these the same size", "Change their header colour", "Make the titles bold"];
}

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 503) return e.message || "This AI connection is unavailable. Select another connection.";
    if (e.status === 429) return e.message || "You've reached the hourly limit for AI requests. Please try again a bit later.";
    if (e.status === 502 || e.status === 504) return e.message || "The selected AI failed to answer. Your dashboard was not changed.";
    if (e.status === 401) return "Your session has expired. Please sign in again.";
    if (e.status === 403 || e.status === 404) return "You can't use the AI assistant on this report.";
    return e.message || "Something went wrong. Your dashboard was not changed.";
  }
  return "I couldn't reach the server. Your dashboard was not changed - please try again.";
}

export function AiChatPanel({ open, mode, reportId, pageName, selectedWidgets, getSnapshot, onApply, onUndo, onClose }: Props) {
  const api = mode === "admin" ? adminApi : ownerApi;
  const [messages, setMessages] = useState<Msg[]>(() => loadMessages(reportId));
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [connections, setConnections] = useState<AiConnection[]>([]);
  const [connectionId, setConnectionId] = useState("");
  const [connectionError, setConnectionError] = useState(false);
  const selectedConnection = connections.find((c) => c.id === connectionId);
  const canSend = !connectionId || (!!selectedConnection?.online && !connectionError);
  const connectionName = selectedConnection
    ? `${selectedConnection.provider === "codex" ? "Codex" : selectedConnection.provider === "claude" ? "Claude" : "AI"} — ${selectedConnection.name}`
    : "Claude — server connection";

  useEffect(() => {
    if (!open) return;
    let active = true;
    const refresh = () => void listAiConnections().then((items) => {
      if (active) { setConnections(items); setConnectionError(false); }
    }).catch(() => { if (active) setConnectionError(true); });
    refresh();
    const timer = window.setInterval(refresh, 10_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [open]);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => saveMessages(reportId, messages), [reportId, messages]);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, open]);

  useEffect(() => {
    if (!busy) return;
    setElapsed(0);
    const started = Date.now();
    const t = window.setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(t);
  }, [busy]);

  // Leaving the designer cancels a request that is still running.
  useEffect(() => () => abortRef.current?.abort(), []);

  const lastAppliedId = useMemo(() => [...messages].reverse().find((m) => m.applied && !m.undone)?.id, [messages]);
  const chips = useMemo(() => suggestionsFor(selectedWidgets), [selectedWidgets]);

  const context =
    selectedWidgets.length === 0
      ? `Whole page: ${pageName}`
      : selectedWidgets.length === 1
        ? `Selected: ${selectedWidgets[0].title || selectedWidgets[0].type}`
        : `Selected: ${selectedWidgets.length} visuals`;

  const send = useCallback(
    async (raw: string) => {
      const message = raw.trim().slice(0, 2000);
      if (!message || busy || !canSend) return;
      const snap = getSnapshot();
      if (!snap) return;
      const history: AiChatHistoryItem[] = messages
        .filter((m) => !m.error)
        .slice(-8)
        .map((m) => ({ role: m.role, text: m.text.slice(0, 1500) }));
      setMessages((prev) => [...prev, { id: uid(), role: "user", text: message }]);
      setText("");
      setBusy(true);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const payload = {
          message,
          definition: snap.def as unknown as Record<string, unknown>,
          page_id: snap.pageId || null,
          selected_widget_ids: selectedWidgets.map((w) => w.id),
          history,
        };
        const res = connectionId
          ? await chatWithConnection(connectionId, reportId, payload, controller.signal)
          : await api.aiChat(reportId, payload, controller.signal);
        if (controller.signal.aborted) throw new DOMException("Cancelled", "AbortError");
        const changed = !!res.definition && res.changes.length > 0;
        if (changed) onApply(snap.def, res.definition as unknown as SDef, res.changes.length);
        setMessages((prev) => [
          ...prev.map((m) => (m.applied && changed ? { ...m, applied: false } : m)),
          { id: uid(), role: "assistant", text: res.reply, changes: res.changes, warnings: res.warnings, applied: changed, connectionName },
        ]);
      } catch (e) {
        if (controller.signal.aborted) {
          setMessages((prev) => [...prev, { id: uid(), role: "assistant", text: "Cancelled - nothing was changed.", error: true }]);
        } else {
          setMessages((prev) => [...prev, { id: uid(), role: "assistant", text: friendlyError(e), error: true }]);
        }
      } finally {
        abortRef.current = null;
        setBusy(false);
        window.setTimeout(() => inputRef.current?.focus(), 30);
      }
    },
    [api, busy, canSend, connectionId, connectionName, getSnapshot, messages, onApply, reportId, selectedWidgets],
  );

  const undo = (id: string) => {
    if (onUndo()) setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, undone: true } : m)));
  };

  return (
    <aside className={`st-ai${open ? " is-open" : ""}`} aria-label="Ask AI" aria-hidden={!open}>
      <header className="st-ai__head">
        <span className="st-ai__badge">
          <Sparkles size={15} />
        </span>
        <div className="st-ai__title">
          <strong>Ask AI</strong>
          <small>Edits this dashboard from plain language</small>
        </div>
        <button className="st-ai__x" onClick={onClose} title="Close (Ctrl+/)" aria-label="Close Ask AI">
          <X size={16} />
        </button>
      </header>

      <div className="st-ai__connection">
        <label htmlFor="st-ai-connection">AI connection</label>
        <select id="st-ai-connection" value={connectionId} disabled={busy} onChange={(e) => setConnectionId(e.target.value)}>
          <option value="">Claude — server connection</option>
          {connections.map((c) => (
            <option key={c.id} value={c.id}>
              {c.provider === "codex" ? "Codex" : c.provider === "claude" ? "Claude" : "AI"} — {c.name} ({c.token_prefix}…) — {c.online ? "Bridge online" : "Offline"}
            </option>
          ))}
        </select>
        <small role="status">
          {connectionError ? "Couldn't refresh your connections. Please try again." : connectionId
            ? selectedConnection?.online ? "Uses this reporting token and the connected AI account." : "This bridge is offline. Start it on the connected computer."
            : "Uses the server's existing Claude subscription."}
        </small>
      </div>

      <div className="st-ai__list" ref={listRef} aria-live="polite">
        {messages.length === 0 ? (
          <div className="st-ai__empty">
            <span className="st-ai__empty-icon">
              <Sparkles size={22} />
            </span>
            <h3>What should I change?</h3>
            <p>
              Describe it the way you would to a colleague - in English or Arabic. I change only what you ask for, and every change is a draft you can undo with
              Ctrl+Z. Nothing is published.
            </p>
          </div>
        ) : null}
        {messages.map((m) => (
          <div key={m.id} className={`st-ai__msg st-ai__msg--${m.role}${m.error ? " is-error" : ""}`}>
            <div className="st-ai__bubble" dir="auto">
              {m.error ? <TriangleAlert size={14} className="st-ai__bubble-ic" /> : null}
              <span>{m.text}</span>
            </div>
            {m.connectionName ? <small className="st-ai__warn">{m.connectionName}</small> : null}
            {m.changes && m.changes.length > 0 ? (
              <ul className="st-ai__changes" dir="auto">
                {m.changes.map((c, i) => (
                  <li key={i}>
                    <Check size={12} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {m.warnings && m.warnings.length > 0 ? (
              <p className="st-ai__warn" dir="auto">
                {m.warnings.join(" ")}
              </p>
            ) : null}
            {m.changes && m.changes.length > 0 ? (
              <div className="st-ai__actions">
                {m.undone ? (
                  <span className="st-ai__undone">Undone</span>
                ) : m.id === lastAppliedId ? (
                  <button className="st-ai__undo" onClick={() => undo(m.id)} title="Undo this AI change (same as Ctrl+Z)">
                    <Undo2 size={13} /> Undo
                  </button>
                ) : (
                  <span className="st-ai__undone">Applied</span>
                )}
              </div>
            ) : null}
          </div>
        ))}
        {busy ? (
          <div className="st-ai__msg st-ai__msg--assistant">
            <div className="st-ai__bubble st-ai__working">
              <Loader2 size={14} className="ds-spin" />
              <span>
                Working with {connectionName}... {elapsed}s{elapsed >= 25 ? " - waiting for the AI reply" : ""}
              </span>
              <button className="st-ai__cancel" onClick={() => abortRef.current?.abort()}>
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <footer className="st-ai__foot">
        <div className="st-ai__ctx" title="The AI treats these as 'this' / 'it'">
          <span className="st-ai__ctx-dot" />
          {context}
        </div>
        <div className="st-ai__chips">
          {chips.map((c) => (
            <button key={c} className="st-ai__chip" disabled={busy || !canSend} onClick={() => void send(c)}>
              {c}
            </button>
          ))}
        </div>
        <div className="st-ai__compose">
          <textarea
            ref={inputRef}
            dir="auto"
            rows={2}
            value={text}
            maxLength={2000}
            placeholder={selectedWidgets.length ? "Tell me what to change about the selection..." : "Tell me what to change..."}
            aria-label="Message to the AI assistant"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (!e.ctrlKey && !e.metaKey) e.stopPropagation(); // keep designer shortcuts (Delete, arrows...) out of the chat box
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send(text);
              }
            }}
          />
          <button className="st-ai__send" disabled={busy || !canSend || !text.trim()} onClick={() => void send(text)} aria-label="Send" title="Send (Enter)">
            {busy ? <Loader2 size={16} className="ds-spin" /> : <SendHorizontal size={16} />}
          </button>
        </div>
        <p className="st-ai__fine">AI can make mistakes - check the result. Changes stay in your draft until you publish.</p>
      </footer>
    </aside>
  );
}
